import { HttpInterceptorFn, HttpErrorResponse } from '@angular/common/http';
import { inject } from '@angular/core';
import { MessageService } from 'primeng/api';
import { catchError, throwError } from 'rxjs';

/** Human-readable summary for each HTTP status we care about. */
function errorMessage(error: HttpErrorResponse): { summary: string; detail: string } {
  if (error.status === 0) {
    return {
      summary: 'No Connection',
      detail: 'Unable to reach the server. Please check your internet connection and try again.',
    };
  }

  switch (error.status) {
    case 400:
      return {
        summary: 'Bad Request',
        detail: error.error?.message || 'The request could not be understood. Please try again.',
      };
    case 403:
      return {
        summary: 'Access Denied',
        detail: "You don't have permission to perform this action.",
      };
    case 404:
      return {
        summary: 'Not Found',
        detail: 'The requested resource could not be found.',
      };
    case 408:
      return {
        summary: 'Request Timeout',
        detail: 'The server took too long to respond. Please try again.',
      };
    case 409:
      return {
        summary: 'Conflict',
        detail: error.error?.message || 'This action conflicts with existing data.',
      };
    case 422:
      return {
        summary: 'Validation Error',
        detail: error.error?.message || 'Some fields are invalid. Please review your input.',
      };
    case 429:
      return {
        summary: 'Too Many Requests',
        detail: 'You have made too many requests. Please wait a moment and try again.',
      };
    case 500:
      return {
        summary: 'Server Error',
        detail: 'Something went wrong on our end. Please try again in a moment.',
      };
    case 502:
    case 503:
    case 504:
      return {
        summary: 'Service Unavailable',
        detail: 'The service is temporarily unavailable. Please try again shortly.',
      };
    default:
      return {
        summary: `Error ${error.status}`,
        detail: error.error?.message || 'An unexpected error occurred. Please try again.',
      };
  }
}

export const errorInterceptor: HttpInterceptorFn = (req, next) => {
  const messageService = inject(MessageService);

  return next(req).pipe(
    catchError((error: unknown) => {
      if (error instanceof HttpErrorResponse) {
        // 401 is owned by the auth interceptor (token refresh / force-logout).
        // Swallow it here to avoid double-toasting.
        if (error.status === 401) {
          return throwError(() => error);
        }

        const { summary, detail } = errorMessage(error);

        messageService.add({
          key: 'global-error',
          severity: 'error',
          summary,
          detail,
          life: 6000,
          closable: true,
        });
      }

      return throwError(() => error);
    }),
  );
};
